<template>
  <v-row class="mb-2">
    <v-col
      sm="12"
      class="text-left d-flex justify-sm-space-between">
      <qx-label
        v-if="label"
        :id="id"
        :label="label"
        :is-vertical="isVertical"
        :info-icon-id="infoIconId"
        :info-icon-position="infoIconPosition"
        :info-icon-title="infoIconTitle"
        :info-icon-is-html="infoIconIsHtml" />
      <slot name="label-right" />
    </v-col>
    <v-col
      :sm="blockWidth"
      class="d-flex">
      <qx-input
        v-if="rowType === 'input'"
        :id="id"
        v-model="innerValue"
        class="flex-fill"
        :name="name"
        :rules="rules"
        :mode="validationMode"
        :error-id="`${id}-validation-error`"
        :disabled="disabled"
        :type="type"
        :trim="trim"
        :readonly="readonly"
        :directive="directive"
        :max-length="maxLength"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :validator-text="validatorText"
        :autofocus="autofocus"
        :on-focus="onFocus"
        :on-blur="onBlur"
        :on-change="onChange"
        :enable-error-block="enableErrorBlock"
        :on-input="onInput">
        <template
          v-if="prependSlot"
          #prepend>
          <slot name="prepend" />
        </template>
        <template
          v-if="iconSlot"
          #icon>
          <slot name="icon" />
        </template>
      </qx-input>
      <qx-select
        v-if="rowType === 'select'"
        :id="id"
        v-model="innerValue"
        class="flex-fill"
        :rules="rules"
        :placeholder="placeholder"
        :disabled="disabled"
        :options="options"
        :no-error-block="!enableErrorBlock"
        :searchable="searchable"
        :group-values="groupValues"
        :group-label="groupLabel"
        :max-height="maxHeight"
        :on-change="onChange"
        :should-sort="shouldSortSelectOptions" />
      <qx-text-area
        v-if="rowType === 'textarea'"
        :id="id"
        v-model="innerValue"
        class="flex-fill"
        :error-id="`${id}-validation-error`"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :rules="rules"
        :trim="trim"
        :name="name"
        :rows="textareaRows" />
      <slot name="right-input" />
    </v-col>
    <slot name="right-cols" />
  </v-row>
</template>

<script>
import QxLabel from '@/elements/QxLabel.vue';
import QxInput from '@/elements/QxInput.vue';
import QxSelect from '@/elements/QxSelect.vue';
import QxTextArea from '@/elements/QxTextArea.vue';

export default {
  name: 'QxInputRow',
  components: {
    QxTextArea,
    QxSelect,
    QxLabel,
    QxInput,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    modelValue: {
      type: [String, Number, Array],
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: [String, Object],
      default: '',
    },
    infoIconId: {
      type: String,
      default: '',
    },
    infoIconPosition: {
      type: String,
      default: 'end center',
    },
    infoIconTitle: {
      type: String,
      default: '',
    },
    infoIconIsHtml: {
      type: Boolean,
      default: false,
    },
    rowType: {
      type: String,
      default: 'input',
      validator: (value) => ['input', 'select', 'textarea'].includes(value),
    },
    options: {
      type: Array,
      default: () => [],
    },
    blockWidth: {
      type: Number,
      default: 12,
    },
    isVertical: {
      type: Boolean,
      default: true,
    },
    autocomplete: {
      type: String,
      default: 'off',
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    directive: {
      type: String,
      default: '',
    },
    maxLength: {
      type: [Number, null],
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    textareaRows: {
      type: Number,
      default: 3,
    },
    trim: {
      type: Boolean,
      default: false,
    },
    validationMode: {
      type: String,
      default: 'validDirty',
      validator: (value) => ['aggressive', 'passive', 'lazy', 'eager', 'validDirty'].includes(value),
    },
    name: {
      type: String,
      required: true,
    },
    validatorText: {
      type: String,
      default: null,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    groupValues: {
      type: String,
      default: '',
    },
    groupLabel: {
      type: String,
      default: '',
    },
    enableErrorBlock: {
      type: Boolean,
      default: true,
    },
    onFocus: {
      type: Function,
      default: () => {},
    },
    onBlur: {
      type: Function,
      default: () => {},
    },
    onInput: {
      type: Function,
      default: () => {},
    },
    onChange: {
      type: Function,
      default: () => {},
    },
    maxHeight: {
      type: Number,
      default: 150,
    },
    shouldSortSelectOptions: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:modelValue'],
  computed: {
    innerValue: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    prependSlot() {
      return this.$slots.prepend;
    },
    iconSlot() {
      return this.$slots.icon;
    },
  },
};
</script>
